import { useState, useEffect, useCallback } from "react";
import { X, LogOut, Mail, Lock, ArrowRight, Loader2, Package, User, MessageCircle, ExternalLink, Shield } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { lockScroll, unlockScroll } from "@/lib/scroll-lock";
import { usePrivacyPreferences } from "@/contexts/PrivacyPreferencesContext";
import { toast } from "sonner";

interface AccountDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Order {
  name: string;
  created_at: string;
  financial_status: string;
  fulfillment_status: string | null;
  total_price: string;
  currency: string;
  line_items: { title: string; quantity: number; price: string }[];
  tracking_url: string | null;
  tracking_number: string | null;
}

const statusLabel = (order: Order) => {
  if (order.fulfillment_status === "fulfilled") return { text: "Delivered", cls: "bg-accent/15 text-accent" };
  if (order.fulfillment_status === "partial") return { text: "Shipped", cls: "bg-primary/15 text-primary" };
  return { text: "Processing", cls: "bg-muted text-muted-foreground" };
};

const AccountDrawer = ({ isOpen, onClose }: AccountDrawerProps) => {
  const { user, loading, signIn, signUp, signOut, resetPassword } = useAuth();
  const { profile, loading: profileLoading, saving, updateProfile } = useProfile();
  const { openPreferences } = usePrivacyPreferences();

  const [mode, setMode] = useState<"login" | "register" | "forgot">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Logged-in state
  const [tab, setTab] = useState<"orders" | "details">("orders");
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [form, setForm] = useState(profile);

  useEffect(() => { setForm(profile); }, [profile]);

  useEffect(() => {
    if (isOpen) {
      lockScroll();
    } else {
      unlockScroll();
      setEmail("");
      setPassword("");
      setError("");
      setMessage("");
      setMode("login");
    }
    return () => { unlockScroll(); };
  }, [isOpen]);

  // Fetch orders when user is logged in and drawer opens
  useEffect(() => {
    if (!isOpen || !user) return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/get-customer-orders`,
          {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const json = await res.json();
        setOrders(json.orders || []);
      } catch {
        console.error("Failed to fetch orders");
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        const { error } = await signIn(email, password);
        if (error) setError(error.message);
      } else if (mode === "register") {
        const { error } = await signUp(email, password);
        if (error) setError(error.message);
        else setMessage("Check your email to confirm your account.");
      } else {
        const { error } = await resetPassword(email);
        if (error) setError(error.message);
        else setMessage("Password reset link sent to your email.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveProfile = async () => {
    const { error } = await updateProfile(form);
    if (error) toast.error("Failed to save profile");
    else toast.success("Profile saved");
  };

  const inputCls = "w-full h-11 bg-input border border-border pl-3 pr-3 text-foreground body-small outline-none focus:border-accent transition-colors";

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-background/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 right-0 bottom-0 z-[70] w-full max-w-[420px] bg-background border-l border-border flex flex-col transition-transform duration-300 ease-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 border-b border-border" style={{ height: "var(--header-height)" }}>
          <h2 className="heading-small text-foreground">
            {user ? "My Account" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Reset Password"}
          </h2>
          <button onClick={onClose} className="text-foreground hover:text-accent transition-colors" aria-label="Close">
            <X size={20} strokeWidth={1.4} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          ) : user ? (
            <div className="flex flex-col h-full">
              {/* Tabs */}
              <div className="flex border-b border-border">
                <button
                  onClick={() => setTab("orders")}
                  className={`flex-1 py-3 body-small transition-colors flex items-center justify-center gap-1.5 ${tab === "orders" ? "text-foreground border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Package size={14} strokeWidth={1.4} />
                  Orders
                </button>
                <button
                  onClick={() => setTab("details")}
                  className={`flex-1 py-3 body-small transition-colors flex items-center justify-center gap-1.5 ${tab === "details" ? "text-foreground border-b-2 border-accent" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <User size={14} strokeWidth={1.4} />
                  Details
                </button>
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {tab === "orders" && (
                  <div className="space-y-4">
                    {ordersLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={20} className="animate-spin text-muted-foreground" />
                      </div>
                    ) : orders.length === 0 ? (
                      <div className="text-center py-12">
                        <Package size={32} className="mx-auto text-muted-foreground/40 mb-3" strokeWidth={1} />
                        <p className="body-small text-muted-foreground">No orders yet</p>
                      </div>
                    ) : (
                      orders.map((order) => {
                        const status = statusLabel(order);
                        return (
                          <div key={order.name} className="border border-border p-4 space-y-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="body-small font-medium text-foreground">{order.name}</p>
                                <p className="body-small text-muted-foreground">
                                  {new Date(order.created_at).toLocaleDateString()} · {order.currency} {order.total_price}
                                </p>
                              </div>
                              <span className={`text-[11px] px-2 py-0.5 rounded-sm ${status.cls}`}>{status.text}</span>
                            </div>
                            <div className="space-y-1">
                              {order.line_items.map((item, i) => (
                                <p key={i} className="body-small text-muted-foreground">
                                  {item.quantity}× {item.title}
                                </p>
                              ))}
                            </div>
                            {order.tracking_url && (
                              <a
                                href={order.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 body-small text-accent hover:underline"
                              >
                                Track shipment <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {tab === "details" && (
                  <div className="space-y-4">
                    {profileLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 size={20} className="animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="body-small text-muted-foreground">First Name</label>
                            <input className={inputCls} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                          </div>
                          <div className="space-y-1.5">
                            <label className="body-small text-muted-foreground">Last Name</label>
                            <input className={inputCls} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="body-small text-muted-foreground">Phone</label>
                          <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <label className="body-small text-muted-foreground">Address</label>
                          <input className={inputCls} value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <label className="body-small text-muted-foreground">City</label>
                            <input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
                          </div>
                          <div className="space-y-1.5">
                            <label className="body-small text-muted-foreground">Postal Code</label>
                            <input className={inputCls} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="body-small text-muted-foreground">Country</label>
                          <input className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
                        </div>
                        <button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="btn w-full h-12 bg-accent text-accent-foreground hover:bg-accent-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save Changes"}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Fixed bottom */}
              <div className="border-t border-border px-6 py-4 space-y-2">
                <a
                  href="https://wa.me/972000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn w-full h-11 border border-border text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} strokeWidth={1.4} />
                  Contact Us
                </a>
                <button
                  onClick={() => { onClose(); openPreferences(); }}
                  className="btn w-full h-11 border border-border text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2"
                >
                  <Shield size={16} strokeWidth={1.4} />
                  Privacy Preferences
                </button>
                <button
                  onClick={signOut}
                  className="btn w-full h-11 text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-2"
                >
                  <LogOut size={14} strokeWidth={1.4} />
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            /* Login / Register / Forgot form — unchanged */
            <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
              <div className="space-y-1.5">
                <label className="body-small text-muted-foreground">Email</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full h-11 bg-input border border-border pl-10 pr-3 text-foreground body-small outline-none focus:border-accent transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {mode !== "forgot" && (
                <div className="space-y-1.5">
                  <label className="body-small text-muted-foreground">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full h-11 bg-input border border-border pl-10 pr-3 text-foreground body-small outline-none focus:border-accent transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              )}

              {error && <p className="body-small text-destructive">{error}</p>}
              {message && <p className="body-small text-accent">{message}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="btn w-full h-12 bg-accent text-accent-foreground hover:bg-accent-hover transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>
                    {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
                    <ArrowRight size={14} />
                  </>
                )}
              </button>

              <div className="space-y-2 pt-2">
                {mode === "login" && (
                  <>
                    <button type="button" onClick={() => setMode("forgot")} className="body-small text-muted-foreground hover:text-foreground transition-colors block">
                      Forgot password?
                    </button>
                    <button type="button" onClick={() => setMode("register")} className="body-small text-muted-foreground hover:text-foreground transition-colors block">
                      Don't have an account? <span className="text-accent">Create one</span>
                    </button>
                  </>
                )}
                {mode === "register" && (
                  <button type="button" onClick={() => setMode("login")} className="body-small text-muted-foreground hover:text-foreground transition-colors block">
                    Already have an account? <span className="text-accent">Sign in</span>
                  </button>
                )}
                {mode === "forgot" && (
                  <button type="button" onClick={() => setMode("login")} className="body-small text-muted-foreground hover:text-foreground transition-colors block">
                    Back to sign in
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default AccountDrawer;
